'use client';

import { useState, type FormEvent } from 'react';
import { Button } from '@open-design/components';
import { useAuth } from './AuthContext';
import { LegalNotice } from './LegalNotice';
import styles from './LoginView.module.css';
import { Crie3Wordmark } from '../components/Crie3Wordmark';

type Mode = 'in' | 'up' | 'reset';

// Hosted-Brazil surface: copy is pt-BR (the product's primary audience). If
// Crie3 later internationalizes, these move behind the i18n Dict.
const COPY: Record<Mode, { headline: string; tagline: string; submit: string }> = {
  in: {
    headline: 'Bem-vindo de volta.',
    tagline: 'Entre para continuar criando com propósito.',
    submit: 'Entrar',
  },
  up: {
    headline: 'Comece agora.',
    tagline: 'Crie sua conta e comece a produzir conteúdo com propósito.',
    submit: 'Criar conta',
  },
  reset: {
    headline: 'Esqueceu a senha?',
    tagline: 'Informe seu e-mail e enviamos um link para você redefinir a senha.',
    submit: 'Enviar link de redefinição',
  },
};

export interface LoginViewProps {
  /** Which tab to open on. The sales page's two CTAs pick different ones. */
  initialMode?: Mode;
  /** Back to the sales page. Absent when there is nothing to go back to. */
  onBack?: () => void;
}

export function LoginView({ initialMode = 'in', onBack }: LoginViewProps = {}) {
  const { signIn, signUp, resetPassword } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [signupSent, setSignupSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    if (mode === 'reset') {
      const { error } = await resetPassword(email.trim());
      setBusy(false);
      // Neutral message (never reveal whether the e-mail exists), even on a
      // rate-limit error; only a hard (network) error surfaces.
      if (error && !/rate|limit|seconds/i.test(error)) setError(error);
      else setResetSent(true);
      return;
    }
    if (mode === 'up') {
      const { error, pendingConfirmation } = await signUp(email.trim(), password);
      setBusy(false);
      if (error) setError(error);
      else if (pendingConfirmation) setSignupSent(true);
      // else: signUp already established a session (email confirmation is
      // off); AuthGate swaps this view out on its own once it observes it.
      return;
    }
    const { error } = await signIn(email.trim(), password);
    if (error) setError(error);
    setBusy(false);
  }

  const copy = COPY[mode];

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        {onBack ? (
          <button type="button" className={styles.back} onClick={onBack}>
            ← Voltar
          </button>
        ) : null}
        <div className={styles.brand}>
                    <Crie3Wordmark className={styles.brandName} height="32px" />
        </div>

        <h1 className={styles.headline}>{copy.headline}</h1>
        <p className={styles.tagline}>{copy.tagline}</p>

        {mode === 'reset' && resetSent ? (
          <>
            <p className={styles.sent}>
              Se existe uma conta com <strong>{email.trim()}</strong>, o link de
              redefinição já está a caminho. Confira também o spam.
            </p>
            <Button
              variant="primary"
              className={styles.submit}
              onClick={() => {
                setMode('in');
                setError(null);
                setResetSent(false);
              }}
            >
              Voltar para o login
            </Button>
          </>
        ) : mode === 'up' && signupSent ? (
          <>
            <p className={styles.sent}>
              Enviamos um link de confirmação para <strong>{email.trim()}</strong>.
              Clique nele para ativar sua conta e depois entre normalmente. Confira
              também o spam.
            </p>
            <Button
              variant="primary"
              className={styles.submit}
              onClick={() => {
                setMode('in');
                setError(null);
                setSignupSent(false);
              }}
            >
              Voltar para o login
            </Button>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className={styles.fields}>
              <input
                type="email"
                className={styles.field}
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                required
                disabled={busy}
              />
              {mode !== 'reset' && (
                <input
                  type="password"
                  className={styles.field}
                  placeholder={mode === 'in' ? 'Senha' : 'Senha (mínimo 6 caracteres)'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
                  required
                  minLength={6}
                  disabled={busy}
                />
              )}
            </div>

            {mode === 'in' && (
              <div className={styles.forgotRow}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setMode('reset');
                    setError(null);
                    setResetSent(false);
                    setSignupSent(false);
                  }}
                >
                  Esqueci minha senha
                </Button>
              </div>
            )}

            {error && <p className={styles.error}>{error}</p>}

            <Button type="submit" variant="primary" className={styles.submit} disabled={busy}>
              {busy ? '…' : copy.submit}
            </Button>
          </form>
        )}

        {mode === 'reset' ? (
          !resetSent && (
            <p className={styles.switch}>
              Lembrou a senha?{' '}
              <Button
                variant="ghost"
                onClick={() => {
                  setMode('in');
                  setError(null);
                  setResetSent(false);
                }}
              >
                Voltar para o login
              </Button>
            </p>
          )
        ) : (
          !signupSent && (
            <p className={styles.switch}>
              {mode === 'in' ? 'Não tem conta?' : 'Já tem conta?'}{' '}
              <Button
                variant="ghost"
                onClick={() => {
                  setMode((m) => (m === 'in' ? 'up' : 'in'));
                  setError(null);
                  setSignupSent(false);
                }}
              >
                {mode === 'in' ? 'Criar conta' : 'Entrar'}
              </Button>
            </p>
          )
        )}

        <LegalNotice />
      </div>
    </div>
  );
}
